/**
 * Utility functions for debugging sales revenue calculations
 */

/**
 * Validates and logs sales order data for revenue calculation debugging
 * @param {Array} orders - Array of sales orders
 * @param {string} timeRange - Time range filter applied
 * @returns {Object} Analysis results
 */
export const analyzeSalesRevenue = (orders, timeRange) => {
  console.group(`🔍 Sales Revenue Analysis - ${timeRange}`);
  
  if (!Array.isArray(orders)) {
    console.error('❌ Orders is not an array:', orders);
    console.groupEnd();
    return { error: 'Invalid orders data' };
  }

  console.log(`📊 Total orders fetched: ${orders.length}`);
  
  // Group orders by status
  const statusGroups = orders.reduce((groups, order) => {
    const status = order.status || 'UNKNOWN';
    groups[status] = (groups[status] || 0) + 1;
    return groups;
  }, {});
  
  console.log('📈 Orders by Status:', statusGroups);
  
  // Group orders by payment status
  const paymentGroups = orders.reduce((groups, order) => {
    const paymentStatus = order.paymentStatus || 'UNKNOWN';
    groups[paymentStatus] = (groups[paymentStatus] || 0) + 1;
    return groups;
  }, {});
  
  console.log('💳 Orders by Payment Status:', paymentGroups);
  
  // Analyze revenue-eligible orders
  const revenueEligibleOrders = orders.filter(order => {
    const validPaymentStatus = order.paymentStatus === 'PAID' || order.paymentStatus === 'PARTIAL';
    const validOrderStatus = order.status !== 'RETURNED' && order.status !== 'CANCELLED';
    return validPaymentStatus && validOrderStatus;
  });
  
  console.log(`✅ Revenue-eligible orders: ${revenueEligibleOrders.length}`);
  
  // Calculate total revenue
  let totalRevenue = 0;
  const revenueBreakdown = revenueEligibleOrders.map(order => {
    const amount = Number(order.total) || 0;
    totalRevenue += amount;
    return {
      orderNumber: order.orderNumber,
      status: order.status,
      paymentStatus: order.paymentStatus,
      total: amount,
      orderDate: order.orderDate
    };
  });
  
  console.log('💰 Revenue Breakdown:', revenueBreakdown);
  console.log(`💵 Total Revenue: $${totalRevenue.toLocaleString()}`);
  
  // Identify excluded orders
  const excludedOrders = orders.filter(order => {
    const validPaymentStatus = order.paymentStatus === 'PAID' || order.paymentStatus === 'PARTIAL';
    const validOrderStatus = order.status !== 'RETURNED' && order.status !== 'CANCELLED';
    return !(validPaymentStatus && validOrderStatus);
  });
  
  if (excludedOrders.length > 0) {
    console.log('❌ Excluded orders:', excludedOrders.map(order => ({
      orderNumber: order.orderNumber,
      status: order.status,
      paymentStatus: order.paymentStatus,
      total: order.total,
      reason: getExclusionReason(order)
    })));
  }
  
  console.groupEnd();
  
  return {
    totalOrders: orders.length,
    revenueEligibleOrders: revenueEligibleOrders.length,
    excludedOrders: excludedOrders.length,
    totalRevenue,
    statusGroups,
    paymentGroups,
    revenueBreakdown
  };
};

/**
 * Gets the reason why an order was excluded from revenue calculation
 * @param {Object} order - Sales order object
 * @returns {string} Exclusion reason
 */
const getExclusionReason = (order) => {
  const reasons = [];
  
  if (order.paymentStatus !== 'PAID' && order.paymentStatus !== 'PARTIAL') {
    reasons.push(`Payment status: ${order.paymentStatus}`);
  }
  
  if (order.status === 'RETURNED') {
    reasons.push('Order returned');
  }
  
  if (order.status === 'CANCELLED') {
    reasons.push('Order cancelled');
  }
  
  return reasons.join(', ') || 'Unknown reason';
};

/**
 * Validates sales order data structure
 * @param {Object} order - Sales order object
 * @returns {Object} Validation result
 */
export const validateSalesOrder = (order) => {
  const issues = [];
  
  if (!order.orderNumber) issues.push('Missing order number');
  if (!order.status) issues.push('Missing status');
  if (!order.paymentStatus) issues.push('Missing payment status');
  if (order.total === undefined || order.total === null) issues.push('Missing total amount');
  if (!order.orderDate) issues.push('Missing order date');
  
  return {
    isValid: issues.length === 0,
    issues
  };
};

/**
 * Compares expected vs actual revenue calculation
 * @param {Array} orders - Array of sales orders
 * @param {number} actualRevenue - Calculated revenue from dashboard
 * @returns {Object} Comparison result
 */
export const compareRevenueCalculation = (orders, actualRevenue) => {
  const analysis = analyzeSalesRevenue(orders, 'Comparison');
  const expectedRevenue = analysis.totalRevenue;
  const difference = Math.abs(expectedRevenue - actualRevenue);
  const percentageDiff = expectedRevenue > 0 ? (difference / expectedRevenue) * 100 : 0;
  
  return {
    expectedRevenue,
    actualRevenue,
    difference,
    percentageDiff,
    isAccurate: difference < 0.01, // Within 1 cent
    analysis
  };
}; 